import { FIXED_BROADBAND_SUBCATEGORIES, OfferSubCategory } from '../enums';
import { getLocale } from '../i18n';
import { eurosToCents } from './priceUtils.js';
import type { PriceType } from '../../components/HorizontalSubscriptionCard/types.js';
import type { BundleAddonGroup } from '../../generated/api/bundleAddonGroup.js';
import type { Extensions } from '../../generated/api/extensions.js';
import type { LocalizedField } from '../../generated/api/localizedField.js';
import type { ProductOfferingCharacteristic } from '../../generated/api/productOfferingCharacteristic.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { ProductSpecification } from '../../generated/api/productSpecification.js';

export const parseSpeedToMbps = (speed?: string): number => {
  if (!speed) {
    return 0;
  }
  const match = /(\d+(?:\.\d+)?)\s*(Mbps|M)?/i.exec(speed);
  return match ? Number.parseFloat(match[1]) : 0;
};

export const getPriceFromType = (
  productOfferingPrice: ProductOfferingPrice[] | undefined,
  priceType: PriceType
): number => {
  const price = productOfferingPrice?.find(p => p.priceType === priceType)?.priceWithoutTax;
  return price ? eurosToCents(price) : 0;
};

export const getProductOfferingPriceDataFromType = (
  productOfferingPrice: ProductOfferingPrice[] | undefined,
  priceType: PriceType
): ProductOfferingPrice | undefined => productOfferingPrice?.find(p => p.priceType === priceType);

export const getProductOfferingPriceDataWithFallback = (
  productOfferingPrice: ProductOfferingPrice[] | undefined,
  preferredPriceType: ProductOfferingPrice.PriceTypeEnum,
  fallbackPriceType: ProductOfferingPrice.PriceTypeEnum
): ProductOfferingPrice | undefined =>
  productOfferingPrice?.find(price => price.priceType === preferredPriceType) ??
  productOfferingPrice?.find(price => price.priceType === fallbackPriceType);

export const getLocalizedString = (val: string | LocalizedField): string => {
  if (typeof val === 'string') {
    return val;
  }
  const locale = getLocale();
  const localized = val.localizedValue?.find(lv => lv.locale === locale);
  return localized?.value ?? val.value ?? '';
};

export const getSpecValueFromSpecs = (specs: ProductSpecification[], specName: string): string | undefined =>
  specs.find(c => c.name === specName)?.value?.[0]?.value;

const getSpecValueFromBundleAddons = (productOffering: ProductOfferingsResponse, specName: string): string => {
  for (const addon of productOffering.bundleAddonProductOffering ?? []) {
    const value = getSpecValueFromSpecs(addon.productSpecification ?? [], specName);
    if (value) {
      return value;
    }
  }
  return '';
};

const getEffectivePlanSpecifications = (productOffering: ProductOfferingsResponse): ProductSpecification[] =>
  productOffering.planProductOffering?.productSpecification ?? productOffering.productSpecification ?? [];

export const getSpecValueWithAddonPriority = (productOffering: ProductOfferingsResponse, specName: string): string =>
  getSpecValueFromBundleAddons(productOffering, specName) ||
  getSpecValueFromSpecs(getEffectivePlanSpecifications(productOffering), specName) ||
  '';

const getCharacteristicValueFromChars = (
  chars: ProductOfferingCharacteristic[],
  charName: string
): string | undefined => chars.find(c => c.name === charName)?.value?.[0]?.value;

const getCharacteristicValueFromBundleAddons = (
  productOffering: ProductOfferingsResponse,
  charName: string
): string => {
  for (const addon of productOffering.bundleAddonProductOffering ?? []) {
    const value = getCharacteristicValueFromChars(addon.productOfferingCharacteristic ?? [], charName);
    if (value) {
      return value;
    }
  }
  return '';
};

const getEffectivePlanCharacteristics = (productOffering: ProductOfferingsResponse): ProductOfferingCharacteristic[] =>
  productOffering.planProductOffering?.productOfferingCharacteristic ??
  productOffering.productOfferingCharacteristic ??
  [];

export const getCharacteristicValueWithAddonPriority = (
  productOffering: ProductOfferingsResponse,
  charName: string
): string =>
  getCharacteristicValueFromBundleAddons(productOffering, charName) ||
  getCharacteristicValueFromChars(getEffectivePlanCharacteristics(productOffering), charName) ||
  '';

export const getDownloadSpeed = (productOffering: ProductOfferingsResponse): string =>
  getSpecValueWithAddonPriority(productOffering, '5g_apn_speed') ||
  getSpecValueWithAddonPriority(productOffering, '4g_apn_speed');

export const getDownloadSpeedInMbps = (productOffering: ProductOfferingsResponse): number =>
  parseSpeedToMbps(getDownloadSpeed(productOffering));

export const isMandatoryAddon = (addon: Pick<ProductOfferingCoco, 'cardinality'>): boolean =>
  (addon.cardinality?.minNum ?? 0) >= 1 && addon.cardinality?.defaultNum === 1;

export const isMandatoryAddonGroup = (bundleAddonGroup: BundleAddonGroup): boolean =>
  (bundleAddonGroup.cardinality?.minNum ?? 0) >= 1;

export const isFixedBroadbandSubCategory = (subcategory: string): boolean =>
  FIXED_BROADBAND_SUBCATEGORIES.includes(subcategory as (typeof FIXED_BROADBAND_SUBCATEGORIES)[number]);

const singleSelectionSubCategories = new Set<OfferSubCategory>([
  OfferSubCategory.SOFTWARE,
  ...FIXED_BROADBAND_SUBCATEGORIES,
]);

export const getMaxProductSelection = (subcategory: string) =>
  singleSelectionSubCategories.has(subcategory as OfferSubCategory) ? 1 : undefined;

export const hasFixedBroadbandProductInBasket = (
  shoppingBasket: { productOfferings?: Array<{ id?: string }> },
  offerId: string
): boolean => shoppingBasket.productOfferings?.some(item => item.id === offerId) === true;

export const isSoftwareSubCategory = (subcategory: string): boolean => subcategory === OfferSubCategory.SOFTWARE;

export const isSoftwareProduct = (productOffer: ProductOfferingsResponse): boolean =>
  isSoftwareSubCategory(productOffer.productOfferingType?.subcategory ?? '');

export const isInsurancePotSubCategory = (subcategory: string): boolean =>
  subcategory === OfferSubCategory.INSURANCE_POT;

export const isInsuranceProduct = (productOffer: { productOfferingType?: { subcategory?: string } }): boolean =>
  isInsurancePotSubCategory(productOffer.productOfferingType?.subcategory ?? '');

export const filterExtensionsWithValues = (extensions?: Extensions[]): Extensions[] | undefined => {
  const populatedExtensions = extensions?.filter(extension => extension.value?.some(value => value.value?.trim()));
  return populatedExtensions?.length ? populatedExtensions : undefined;
};
